import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  apiUrl,
  formatApiErrorDetail,
  AUTH_REQUEST_TIMEOUT_MS,
  isTransientApiError,
  isAuthSessionError,
  readCachedUser,
} from '@/lib/apiBase';

const AuthContext = createContext(null);

// Cookie HttpOnly + Bearer in localStorage (stesso dominio leadlegale.com)
axios.defaults.withCredentials = true;
const ACCESS_TOKEN_KEY = 'leadlegale_token';
const AUTH_RETRY_DELAYS_MS = [0, 1500, 3000, 5000, 8000];
const AUTH_MAX_ATTEMPTS = AUTH_RETRY_DELAYS_MS.length;
const RECONNECT_POLL_MS = 12000;

function sleep(ms) {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function normalizeUserFromMe(me, fallback = {}) {
  const base = {
    id: String(me?.id || fallback?.id || ''),
    username: String(me?.username || fallback?.username || fallback?.email || ''),
    email: String(me?.email || fallback?.email || ''),
    name: String(me?.name || fallback?.name || me?.username || 'Utente'),
    role: String(me?.role || fallback?.role || ''),
    practice_category: String(me?.practice_category || fallback?.practice_category || 'all'),
  };
  if (['lawyer', 'partner'].includes(String(base.role || '').toLowerCase())) {
    return {
      ...base,
      subscription_plan: String(me?.subscription_plan || fallback?.subscription_plan || 'starter'),
      credits: Number(me?.credits ?? fallback?.credits ?? 0),
      leads_included_remaining: Number(me?.leads_included_remaining ?? fallback?.leads_included_remaining ?? 0),
      leads_included_total: Number(me?.leads_included_total ?? fallback?.leads_included_total ?? 0),
      is_active: me?.is_active ?? fallback?.is_active ?? true,
      subscription_status: String(me?.subscription_status || fallback?.subscription_status || 'active'),
      credits_frozen: Boolean(me?.credits_frozen ?? fallback?.credits_frozen ?? false),
      subscription_expires_at: me?.subscription_expires_at || fallback?.subscription_expires_at || null,
    };
  }
  return base;
}

function setAuthHeaderFromToken(token) {
  if (token && String(token).trim()) {
    axios.defaults.headers.common.Authorization = `Bearer ${String(token).trim()}`;
  } else {
    delete axios.defaults.headers.common.Authorization;
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => readCachedUser());
  const [loading, setLoading] = useState(true);
  const [reconnecting, setReconnecting] = useState(false);
  const [error, setError] = useState(null);
  const checkInFlight = useRef(false);

  const clearSession = useCallback(() => {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem('leadlegale_user');
    setAuthHeaderFromToken(null);
    setUser(false);
    setReconnecting(false);
  }, []);

  const checkAuth = useCallback(async (options = {}) => {
    const { silent = false } = options;
    if (checkInFlight.current) return;
    checkInFlight.current = true;

    if (!silent) {
      setLoading(true);
      setError(null);
    }

    const token = localStorage.getItem(ACCESS_TOKEN_KEY);
    const cachedUser = readCachedUser();
    setAuthHeaderFromToken(token);

    if (!token && !cachedUser) {
      setUser(false);
      setReconnecting(false);
      setLoading(false);
      checkInFlight.current = false;
      return;
    }

    let lastTransientError = null;

    for (let attempt = 0; attempt < AUTH_MAX_ATTEMPTS; attempt += 1) {
      if (attempt > 0) {
        setReconnecting(true);
        await sleep(AUTH_RETRY_DELAYS_MS[attempt] || 5000);
      }

      try {
        const { data } = await axios.get(apiUrl('/api/auth/me'), {
          withCredentials: true,
          timeout: AUTH_REQUEST_TIMEOUT_MS,
          headers: { Accept: 'application/json', 'Cache-Control': 'no-cache' },
        });
        const normalized = normalizeUserFromMe(data, cachedUser || {});
        setUser(normalized);
        localStorage.setItem('leadlegale_user', JSON.stringify(normalized));
        setReconnecting(false);
        setLoading(false);
        checkInFlight.current = false;
        return;
      } catch (e) {
        if (isAuthSessionError(e)) {
          clearSession();
          setLoading(false);
          checkInFlight.current = false;
          return;
        }
        if (isTransientApiError(e)) {
          lastTransientError = e;
          continue;
        }
        if (token || cachedUser) {
          lastTransientError = e;
          continue;
        }
        clearSession();
        setLoading(false);
        checkInFlight.current = false;
        return;
      }
    }

    if (token && cachedUser) {
      setUser(cachedUser);
      setAuthHeaderFromToken(token);
      setReconnecting(true);
      setError(
        formatApiErrorDetail(lastTransientError?.response?.data?.detail)
        || 'Server in avvio o connessione lenta. Riprovo in background…'
      );
    } else {
      clearSession();
    }
    setLoading(false);
    checkInFlight.current = false;
  }, [clearSession]);

  useEffect(() => {
    const storedToken = localStorage.getItem(ACCESS_TOKEN_KEY);
    setAuthHeaderFromToken(storedToken);
    checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    if (!reconnecting) return undefined;
    const intervalId = window.setInterval(() => {
      checkAuth({ silent: true });
    }, RECONNECT_POLL_MS);
    return () => window.clearInterval(intervalId);
  }, [reconnecting, checkAuth]);

  const login = async (email, password) => {
    setError(null);
    setReconnecting(false);
    try {
      const payload = { email: String(email || '').trim(), password };
      const { data } = await axios.post(
        apiUrl('/api/auth/login'),
        payload,
        {
          withCredentials: true,
          timeout: 20000,
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'Cache-Control': 'no-cache',
            Pragma: 'no-cache',
          },
        }
      );
      const token = String(data?.access_token || '').trim();
      if (token) {
        localStorage.setItem(ACCESS_TOKEN_KEY, token);
        setAuthHeaderFromToken(token);
        try {
          const { data: me } = await axios.get(apiUrl('/api/auth/me'), {
            withCredentials: true,
            timeout: AUTH_REQUEST_TIMEOUT_MS,
            headers: { Accept: 'application/json', 'Cache-Control': 'no-cache' },
          });
          const normalizedUser = normalizeUserFromMe(me, data);
          localStorage.setItem('leadlegale_user', JSON.stringify(normalizedUser));
          setUser(normalizedUser);
        } catch {
          const normalizedUser = normalizeUserFromMe(data);
          localStorage.setItem('leadlegale_user', JSON.stringify(normalizedUser));
          setUser(normalizedUser);
        }
        return { success: true };
      }
      localStorage.removeItem(ACCESS_TOKEN_KEY);
      setAuthHeaderFromToken(null);
      try {
        const { data: me } = await axios.get(apiUrl('/api/auth/me'), {
          withCredentials: true,
          timeout: AUTH_REQUEST_TIMEOUT_MS,
          headers: { Accept: 'application/json', 'Cache-Control': 'no-cache' },
        });
        const normalizedUser = normalizeUserFromMe(me);
        setUser(normalizedUser);
        localStorage.setItem('leadlegale_user', JSON.stringify(normalizedUser));
      } catch (meErr) {
        clearSession();
        const errMsg =
          formatApiErrorDetail(meErr.response?.data?.detail)
          || 'Token non valido: sessione non confermata. Riprova il login.';
        setError(errMsg);
        return { success: false, error: errMsg };
      }
      return { success: true };
    } catch (e) {
      const isTimeout = isTransientApiError(e);
      const errorMsg = isTimeout
        ? 'Server in avvio o non raggiungibile. Attendi qualche secondo e riprova.'
        : (formatApiErrorDetail(e.response?.data?.detail) || e.message);
      setError(errorMsg);
      return { success: false, error: errorMsg };
    }
  };

  const logout = async () => {
    try {
      await axios.post(apiUrl('/api/auth/logout'), {});
    } catch (e) {
      console.error('Logout error:', e);
    } finally {
      clearSession();
    }
  };

  const value = {
    user,
    loading,
    reconnecting,
    error,
    login,
    logout,
    checkAuth,
    isAuthenticated: !!user && user !== false,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
